import { API_BASE } from '../../config/apiBase';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { 
  Truck,
  Plus,
  ArrowRight,
  Search
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { cn } from '../../lib/utils';

const API = API_BASE;

const AppraisalList = () => {
  const navigate = useNavigate();
  const [appraisals, setAppraisals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchAppraisals();
  }, []);

  const fetchAppraisals = async () => {
    try {
      const response = await axios.get(`${API}/appraisals`);
      setAppraisals(response.data);
    } catch (error) {
      console.error('Error fetching appraisals:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAppraisals = appraisals.filter(a => 
    `${a.asset?.year} ${a.asset?.make} ${a.asset?.model}`.toLowerCase().includes(search.toLowerCase()) ||
    a.client_name?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="appraisal-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] flex items-center gap-2">
            <Truck className="w-6 h-6 text-blue-600" />
            Tasación de Activos
          </h1>
          <p className="text-muted-foreground">Tasaciones de camiones y equipos</p>
        </div>
        <Button onClick={() => navigate('/apps/appraisal/new')} data-testid="new-appraisal-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nueva Tasación
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
        <Input
          placeholder="Buscar por equipo o cliente..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Appraisals Grid */}
      {filteredAppraisals.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAppraisals.map((appraisal) => (
            <Card 
              key={appraisal.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(`/apps/appraisal/${appraisal.id}`)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      'w-12 h-12 rounded-lg flex items-center justify-center',
                      appraisal.status === 'completed' ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-blue-100 dark:bg-blue-900/30'
                    )}>
                      <Truck className={cn(
                        'w-6 h-6',
                        appraisal.status === 'completed' ? 'text-emerald-600' : 'text-blue-600'
                      )} />
                    </div>
                    <div>
                      <CardTitle className="text-base">
                        {appraisal.asset?.year} {appraisal.asset?.make}
                      </CardTitle>
                      <CardDescription>{appraisal.asset?.model}</CardDescription>
                    </div>
                  </div>
                  <Badge variant={appraisal.status === 'completed' ? 'default' : 'secondary'}>
                    {appraisal.status === 'completed' ? 'Completada' : 'Borrador'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-muted-foreground">Millaje</p>
                    <p className="font-medium">{appraisal.asset?.mileage?.toLocaleString() || 'N/A'} mi</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Valor Tasado</p>
                    <p className="font-medium text-blue-600">
                      ${appraisal.appraised_value?.toLocaleString() || 'Pendiente'}
                    </p>
                  </div>
                </div>
                
                {appraisal.client_name && (
                  <p className="text-sm text-muted-foreground">\ud83d\udc64 {appraisal.client_name}</p>
                )}

                <div className="flex items-center text-sm text-primary font-medium pt-2">
                  Ver detalles
                  <ArrowRight className="w-4 h-4 ml-1" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="py-12">
          <CardContent className="text-center">
            <Truck className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-semibold mb-2">No hay tasaciones</h3>
            <p className="text-muted-foreground mb-4">
              {search ? 'No se encontraron tasaciones con esa búsqueda' : 'Comienza creando tu primera tasación'}
            </p>
            <Button onClick={() => navigate('/apps/appraisal/new')}>
              <Plus className="w-4 h-4 mr-2" />
              Nueva Tasación
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AppraisalList;
